export { CoverEditor } from './cover-editor';
import React from 'react';
import { type Metadata } from './model';
import { LanguagePicker } from './language-picker';
import { DescriptionEditor } from './description-editor';
import { renderDescriptionMarkdown } from './description-markdown';
export const button = 'admin-button';
export const input = 'admin-input mt-2 block w-full';
export const card = 'admin-panel p-5 sm:p-6';
export function MetadataEditor({
  value,
  change,
}: {
  value: Metadata;
  change(value: Metadata): void;
}) {
  const textField = (key: 'title' | 'subtitle' | 'isbn' | 'publisher', label: string) => (
    <label className="block min-w-0 text-sm font-semibold">
      {label}
      <input
        className={input}
        value={value[key] ?? ''}
        required={key === 'title'}
        maxLength={1000}
        onChange={(e) =>
          change({ ...value, [key]: e.target.value || (key === 'title' ? '' : null) })
        }
      />
    </label>
  );
  return (
    <div className="min-w-0 space-y-7">
      <fieldset className="min-w-0">
        <legend className="mb-4 font-serif text-2xl">Book details</legend>
        <div className="grid min-w-0 gap-5 sm:grid-cols-2">
          {textField('title', 'Title')}
          {textField('subtitle', 'Subtitle (optional)')}
          <div className="space-y-3">
            <label className="block text-sm font-semibold">
              Authors (one per line)
              <textarea
                className={input}
                rows={3}
                value={value.authors.join('\n')}
                onChange={(e) => change({ ...value, authors: e.target.value.split('\n') })}
              />
            </label>
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={value.authorUnknown}
                onChange={(e) => change({ ...value, authorUnknown: e.target.checked })}
              />
              Author is unknown
            </label>
          </div>
          <div className="text-sm font-semibold">
            <LanguagePicker
              value={value.language}
              className={input}
              change={(language) => change({ ...value, language })}
            />
          </div>
        </div>
      </fieldset>
      <fieldset className="min-w-0 border-t border-line/65 pt-5">
        <legend className="pr-3 font-serif text-2xl">Edition details</legend>
        <div className="grid min-w-0 gap-5 sm:grid-cols-2">
          {textField('isbn', 'ISBN')}
          {textField('publisher', 'Publisher')}
          {(['publishedYear', 'pageCount'] as const).map((key) => (
            <label key={key} className="text-sm font-semibold">
              {key === 'publishedYear' ? 'Publication year' : 'Page count'}
              <input
                className={input}
                type="number"
                min="1"
                step="1"
                value={value[key] ?? ''}
                onChange={(e) =>
                  change({ ...value, [key]: e.target.value ? Number(e.target.value) : null })
                }
              />
            </label>
          ))}
          <label className="text-sm font-semibold">
            Binding
            <select
              className={input}
              value={value.binding ?? ''}
              onChange={(e) =>
                change({ ...value, binding: (e.target.value || null) as Metadata['binding'] })
              }
            >
              <option value="">Unknown</option>
              {value.binding &&
                !['paperback', 'hardcover', 'ebook', 'other'].includes(value.binding) && (
                  <option value={value.binding}>{value.binding} (current provider value)</option>
                )}
              {['paperback', 'hardcover', 'ebook', 'other'].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
      </fieldset>
      <div className="border-t border-line/65 pt-5 text-sm font-semibold">
        <DescriptionEditor
          value={value.description}
          change={(description) => change({ ...value, description })}
        />
      </div>
    </div>
  );
}

export function MetadataPreview({ value }: { value: Metadata }) {
  return (
    <section
      className="mt-5 rounded-xl bg-panel-raised p-5 text-sm leading-6"
      aria-label="Bibliographic preview"
    >
      <h3 className="break-words font-serif text-2xl leading-tight">{value.title}</h3>
      {value.subtitle && <p>{value.subtitle}</p>}
      <p className="mt-2 text-ink-soft">
        {value.authorUnknown ? 'Unknown author' : value.authors.join(', ')}
      </p>
      <p className="mt-3 text-xs text-ink-muted">
        {[
          value.language,
          value.isbn,
          value.publisher,
          value.publishedYear,
          value.binding,
          value.pageCount && `${value.pageCount} pages`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {value.description && (
        <div
          className="description-markdown mt-4 text-ink-soft"
          dangerouslySetInnerHTML={{ __html: renderDescriptionMarkdown(value.description) }}
        />
      )}
    </section>
  );
}
